/**
 * Read-only viewer for a bot catalog — the user-agent tokens a given category
 * (search-engine crawlers / security scanners / bad bots) matches. Opened by the
 * "eye" icon next to each bot toggle. Backed by `GET /waf/bots?category=`.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useWafBotCatalog, wafExtractError } from '@/api/waf';
import styles from './WafBotManagement.module.css';

export function BotCatalogDialog({
  serviceId,
  category,
  title,
  open,
  onOpenChange,
}: {
  serviceId: string;
  category: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);

  // Only fetch while the dialog is open.
  const catalogQ = useWafBotCatalog(serviceId, open ? category : undefined);
  const entries = catalogQ.data?.entries ?? [];

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? entries.filter((e) => e.toLowerCase().includes(t)) : entries;
  }, [entries, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The bots matched by this rule. This list is maintained by the provider and is read-only.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {catalogQ.isLoading ? (
            <div style={{ display: 'grid', gap: 6 }}>
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 24 }} />)}
            </div>
          ) : catalogQ.isError ? (
            <Alert tone="danger" title="Couldn't load the bot list" actions={<button onClick={() => catalogQ.refetch()}>Retry</button>}>
              {wafExtractError(catalogQ.error, 'The bot catalog failed to load.')}
            </Alert>
          ) : entries.length === 0 ? (
            <Alert tone="info" title="No entries">This category has no bots listed.</Alert>
          ) : (
            <>
              <Input
                placeholder="Filter bots…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Filter bots"
              />
              <div className={styles.catalogList}>
                {filtered.length === 0 ? (
                  <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)', padding: 6 }}>
                    No bots match “{q}”.
                  </span>
                ) : (
                  filtered.map((e) => (
                    <div key={e} className={styles.catalogItem}>{e}</div>
                  ))
                )}
              </div>
              <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                {filtered.length} of {entries.length} bot{entries.length === 1 ? '' : 's'}.
              </span>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export default BotCatalogDialog;
